import React, { useState, useEffect, useMemo } from 'react';
import { useMediaQuery, useTheme, Autocomplete, TextField, TablePagination, Menu, MenuItem, IconButton } from '@mui/material';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { API_CONFIG, authFetch } from '../config';
import { formatDate } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';
import { useAuth } from '../contexts/AuthContext';

interface Lottery {
  id: number;
  name: string;
  name_kurz: string;
  year: number;
  type: string;
}

interface Campaign {
  id: number;
  lottery_id: number;
  name: string;
  lottery_name?: string;
}

interface Werbeweg {
  id: number;
  campaign_id: number;
  name: string;
  campaign_name?: string;
}

interface MarketingDocument {
  id: number;
  document_type: 'werbemittel' | 'gespraechsleitfaden';
  lottery_id: number;
  campaign_id?: number;
  werbeweg_id?: number;
  document_name: string;
  file_path: string;
  file_size: number;
  file_extension: string;
  creation_date?: string;
  author?: string;
  uploaded_by?: string;
  created_at: string;
  lottery_name?: string;
  campaign_name?: string;
  werbeweg_name?: string;
}

// Strip lottery suffix from campaign name (e.g. "Inaktive - SKL 147" -> "Inaktive")
const stripLotterySuffix = (name: string): string => {
  if (!name) return name;
  return name.replace(/\s+-\s+(SKL|NKL|GKL)\s+\d+$/i, '').trim();
};

// CSS Tooltip component that works in Safari
const TruncatedText = ({ text, maxWidth = '170px' }: { text: string; maxWidth?: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({ x: rect.left, y: rect.bottom + 5 });
    setShowTooltip(true);
  };

  return (
    <span
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
      style={{
        display: 'block',
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'default',
        position: 'relative'
      }}
    >
      {text}
      {showTooltip && text && (
        <span
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '0.8rem',
            zIndex: 10000,
            maxWidth: '400px',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default function DocumentManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<'werbemittel' | 'gespraechsleitfaden'>('werbemittel');
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [allWerbewege, setAllWerbewege] = useState<Werbeweg[]>([]);
  const [documents, setDocuments] = useState<MarketingDocument[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedLottery, setSelectedLottery] = useState<number | ''>('');
  const [selectedCampaign, setSelectedCampaign] = useState<number | ''>('');
  const [selectedWerbeweg, setSelectedWerbeweg] = useState<number | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size' | 'author'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    document_name: '',
    creation_date: new Date().toISOString().split('T')[0],
    author: ''
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Auto-set author from logged-in user
  useEffect(() => {
    if (user && !uploadData.author) {
      setUploadData(prev => ({ ...prev, author: `${user.first_name} ${user.last_name}`.trim() }));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit state
  const [editingDocument, setEditingDocument] = useState<MarketingDocument | null>(null);
  const [editData, setEditData] = useState({
    document_name: '',
    creation_date: '',
    author: ''
  });

  // Copy state
  const [copyingDocument, setCopyingDocument] = useState<MarketingDocument | null>(null);
  const [copyData, setCopyData] = useState({
    lottery_id: '',
    campaign_id: '',
    werbeweg_id: ''
  });
  const [copyLotteries, setCopyLotteries] = useState<Lottery[]>([]);
  const [copyCampaigns, setCopyCampaigns] = useState<Campaign[]>([]);
  const [copyWerbewege, setCopyWerbewege] = useState<Werbeweg[]>([]);
  const [copying, setCopying] = useState(false);

  // Bulk selection state
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [bulkCopyDialog, setBulkCopyDialog] = useState(false);

  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuDoc, setActionMenuDoc] = useState<MarketingDocument | null>(null);

  useEffect(() => {
    loadLotteries();
    loadAllCampaigns();
    loadAllWerbewege();
    loadDocuments();
  }, []);

  useEffect(() => {
    // Wenn Lotterie geändert wird, Kampagne und Werbeweg zurücksetzen (nur wenn nicht passend)
    if (selectedLottery && selectedCampaign) {
      const campaign = allCampaigns.find(c => c.id === selectedCampaign);
      if (campaign && campaign.lottery_id !== selectedLottery) {
        setSelectedCampaign('');
        setSelectedWerbeweg('');
      }
    }
  }, [selectedLottery]);

  useEffect(() => {
    // Wenn Kampagne ausgewählt wird, automatisch die passende Lotterie setzen
    if (selectedCampaign) {
      const campaign = allCampaigns.find(c => c.id === selectedCampaign);
      if (campaign && campaign.lottery_id !== selectedLottery) {
        setSelectedLottery(campaign.lottery_id);
      }
    }
  }, [selectedCampaign]);

  useEffect(() => {
    loadDocuments();
  }, [activeTab, selectedLottery, selectedCampaign, selectedWerbeweg]);

  // Gefilterte Kampagnen basierend auf Lotterie-Auswahl
  const filteredCampaigns = useMemo(() => {
    if (!selectedLottery) return allCampaigns;
    return allCampaigns.filter(c => c.lottery_id === selectedLottery);
  }, [allCampaigns, selectedLottery]);

  // Alle Werbewege (keine Filterung nötig - Werbewege sind Stammdaten ohne Kampagnenbezug)
  const filteredWerbewege = allWerbewege;

  const loadLotteries = async () => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/lotteries`);
      const data = await response.json();
      setLotteries(data);
    } catch (error) {
      console.error('Failed to load lotteries:', error);
    }
  };

  const loadAllCampaigns = async () => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/campaigns`);
      const data = await response.json();
      setAllCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadAllWerbewege = async () => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/werbewege`);
      const data = await response.json();
      // Note: werbewege table uses 'bezeichnung' for name
      setAllWerbewege(data.map((w: any) => ({
        id: w.id,
        name: w.bezeichnung || w.name
      })));
    } catch (error) {
      console.error('Failed to load werbewege:', error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('document_type', activeTab);
      if (selectedLottery) params.append('lottery_id', selectedLottery.toString());
      if (selectedCampaign) params.append('campaign_id', selectedCampaign.toString());
      if (selectedWerbeweg) params.append('werbeweg_id', selectedWerbeweg.toString());

      const response = await authFetch(`${API_CONFIG.API_V1}/documents?${params}`);
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadData.document_name) {
        setUploadData({ ...uploadData, document_name: file.name });
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !selectedLottery) {
      setUploadError('Bitte wählen Sie eine Datei und eine Lotterie aus');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', activeTab);
      formData.append('lottery_id', selectedLottery.toString());
      if (selectedCampaign) formData.append('campaign_id', selectedCampaign.toString());
      if (selectedWerbeweg) formData.append('werbeweg_id', selectedWerbeweg.toString());
      if (uploadData.document_name) formData.append('document_name', uploadData.document_name);
      if (uploadData.creation_date) formData.append('creation_date', uploadData.creation_date);
      if (uploadData.author) formData.append('author', uploadData.author);

      const response = await authFetch(`${API_CONFIG.API_V1}/documents`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      // Reset form
      setUploadFile(null);
      setUploadData({ document_name: '', creation_date: new Date().toISOString().split('T')[0], author: user ? `${user.first_name} ${user.last_name}`.trim() : '' });
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Reload documents
      loadDocuments();
    } catch (error) {
      setUploadError((error as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (doc: MarketingDocument) => {
    setEditingDocument(doc);
    setEditData({
      document_name: doc.document_name,
      creation_date: doc.creation_date || '',
      author: doc.author || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingDocument) return;

    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/documents/${editingDocument.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (!response.ok) throw new Error('Failed to update document');

      setEditingDocument(null);
      loadDocuments();
    } catch (error) {
      alert('Fehler beim Aktualisieren: ' + (error as Error).message);
    }
  };

  const handleDelete = async (doc: MarketingDocument) => {
    if (!window.confirm(`Dokument "${doc.document_name}" wirklich löschen?`)) return;

    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/documents/${doc.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete document');

      loadDocuments();
    } catch (error) {
      alert('Fehler beim Löschen: ' + (error as Error).message);
    }
  };

  const handleDownload = async (doc: MarketingDocument) => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/documents/${doc.id}/download`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.document_name || `document_${doc.id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Fehler beim Download: ' + (error as Error).message);
    }
  };

  const handleCopy = async (doc: MarketingDocument) => {
    setCopyingDocument(doc);
    // Pre-populate with current values
    setCopyData({
      lottery_id: doc.lottery_id.toString(),
      campaign_id: doc.campaign_id?.toString() || '',
      werbeweg_id: doc.werbeweg_id?.toString() || ''
    });
    // Load fresh data for dropdowns
    const lotteriesData = await loadLotteriesForCopy();
    setCopyLotteries(lotteriesData);
    if (doc.lottery_id) {
      const campaignsData = await loadCampaignsForCopy(doc.lottery_id);
      setCopyCampaigns(campaignsData);
      if (doc.campaign_id) {
        const werbewegeData = await loadWerbewegeForCopy(doc.campaign_id);
        setCopyWerbewege(werbewegeData);
      }
    }
  };

  const loadLotteriesForCopy = async () => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/lotteries`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to load lotteries:', error);
      return [];
    }
  };

  const loadCampaignsForCopy = async (lotteryId: number) => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/campaigns`);
      const data = await response.json();
      return data.filter((c: Campaign) => c.lottery_id === lotteryId);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      return [];
    }
  };

  const loadWerbewegeForCopy = async (campaignId: number) => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/werbewege`);
      const data = await response.json();
      return data.map((w: any) => ({
        ...w,
        name: w.bezeichnung
      }));
    } catch (error) {
      console.error('Failed to load werbewege:', error);
      return [];
    }
  };

  const handleCopyLotteryChange = async (lotteryId: string) => {
    setCopyData({ ...copyData, lottery_id: lotteryId, campaign_id: '', werbeweg_id: '' });
    if (lotteryId) {
      const campaignsData = await loadCampaignsForCopy(Number(lotteryId));
      setCopyCampaigns(campaignsData);
    } else {
      setCopyCampaigns([]);
    }
    setCopyWerbewege([]);
  };

  const handleCopyCampaignChange = async (campaignId: string) => {
    setCopyData({ ...copyData, campaign_id: campaignId, werbeweg_id: '' });
    if (campaignId) {
      const werbewegeData = await loadWerbewegeForCopy(Number(campaignId));
      setCopyWerbewege(werbewegeData);
    } else {
      setCopyWerbewege([]);
    }
  };

  const handleSaveCopy = async () => {
    if (!copyingDocument || !copyData.lottery_id) {
      alert('Bitte wählen Sie mindestens eine Lotterie aus');
      return;
    }

    setCopying(true);
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/documents/${copyingDocument.id}/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lottery_id: Number(copyData.lottery_id),
          campaign_id: copyData.campaign_id ? Number(copyData.campaign_id) : null,
          werbeweg_id: copyData.werbeweg_id ? Number(copyData.werbeweg_id) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Copy failed');
      }

      setCopyingDocument(null);
      setCopyData({ lottery_id: '', campaign_id: '', werbeweg_id: '' });
      setCopyLotteries([]);
      setCopyCampaigns([]);
      setCopyWerbewege([]);
      loadDocuments();
      alert('Dokument erfolgreich kopiert');
    } catch (error) {
      alert('Fehler beim Kopieren: ' + (error as Error).message);
    } finally {
      setCopying(false);
    }
  };

  // Bulk selection handlers
  const handleSelectDocument = (id: number) => {
    setSelectedDocuments(prev =>
      prev.includes(id)
        ? prev.filter(dId => dId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === filteredDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocuments.map(d => d.id));
    }
  };

  const handleBulkCopyOpen = async () => {
    setBulkCopyDialog(true);
    setCopyData({ lottery_id: '', campaign_id: '', werbeweg_id: '' });
    const lotteriesData = await loadLotteriesForCopy();
    setCopyLotteries(lotteriesData);
    setCopyCampaigns([]);
    setCopyWerbewege([]);
  };

  const handleBulkCopy = async () => {
    if (!copyData.lottery_id) {
      alert('Bitte wählen Sie mindestens eine Lotterie aus');
      return;
    }

    setCopying(true);
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/documents/bulk-copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_ids: selectedDocuments,
          lottery_id: Number(copyData.lottery_id),
          campaign_id: copyData.campaign_id ? Number(copyData.campaign_id) : null,
          werbeweg_id: copyData.werbeweg_id ? Number(copyData.werbeweg_id) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Bulk copy failed');
      }

      const result = await response.json();
      setBulkCopyDialog(false);
      setCopyData({ lottery_id: '', campaign_id: '', werbeweg_id: '' });
      setCopyLotteries([]);
      setCopyCampaigns([]);
      setCopyWerbewege([]);
      setSelectedDocuments([]);
      loadDocuments();
      alert(result.message || `${selectedDocuments.length} Dokument(e) kopiert`);
    } catch (error) {
      alert('Fehler beim Kopieren: ' + (error as Error).message);
    } finally {
      setCopying(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // formatDate is imported from utils/dateUtils

  // Filter and sort documents
  const getFilteredAndSortedDocuments = () => {
    let filtered = [...documents];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.document_name.toLowerCase().includes(term) ||
        doc.author?.toLowerCase().includes(term) ||
        doc.lottery_name?.toLowerCase().includes(term) ||
        doc.campaign_name?.toLowerCase().includes(term) ||
        doc.werbeweg_name?.toLowerCase().includes(term)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let compareA: any;
      let compareB: any;

      switch (sortBy) {
        case 'name':
          compareA = a.document_name.toLowerCase();
          compareB = b.document_name.toLowerCase();
          break;
        case 'date':
          compareA = a.creation_date ? new Date(a.creation_date).getTime() : 0;
          compareB = b.creation_date ? new Date(b.creation_date).getTime() : 0;
          break;
        case 'size':
          compareA = a.file_size || 0;
          compareB = b.file_size || 0;
          break;
        case 'author':
          compareA = (a.author || '').toLowerCase();
          compareB = (b.author || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredDocuments = getFilteredAndSortedDocuments();

  // Paginated documents
  const paginatedDocuments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDocuments.slice(start, start + rowsPerPage);
  }, [filteredDocuments, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedLottery, selectedCampaign, selectedWerbeweg, activeTab]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <SubPageHeader title="BLD – Modul Herkunft" />

      {/* Page Title */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: isMobile ? '1rem' : '1.5rem 2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            color: '#003366',
            fontSize: isMobile ? '1.3rem' : '1.5rem',
            fontWeight: '600'
          }}>
            📄 BLD – Modul Herkunft
          </h2>
          <p style={{
            margin: 0,
            color: '#6b7280',
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}>
            Speicherung von Dokumentationen (Werbemittel & Gesprächsleitfäden)
          </p>
        </div>
      </div>

      <main style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        {/* Tabs */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          marginBottom: isMobile ? '1rem' : '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <button
              onClick={() => setActiveTab('werbemittel')}
              style={{
                flex: 1,
                padding: isMobile ? '1rem' : '1.25rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'werbemittel' ? '3px solid #003366' : 'none',
                color: activeTab === 'werbemittel' ? '#003366' : '#6b7280',
                fontWeight: activeTab === 'werbemittel' ? '600' : '400',
                fontSize: isMobile ? '0.95rem' : '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Werbemittel
            </button>
            <button
              onClick={() => setActiveTab('gespraechsleitfaden')}
              style={{
                flex: 1,
                padding: isMobile ? '1rem' : '1.25rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'gespraechsleitfaden' ? '3px solid #003366' : 'none',
                color: activeTab === 'gespraechsleitfaden' ? '#003366' : '#6b7280',
                fontWeight: activeTab === 'gespraechsleitfaden' ? '600' : '400',
                fontSize: isMobile ? '0.95rem' : '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Gesprächsleitfäden
            </button>
          </div>
        </div>

        {/* Filters & Upload */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '1rem' : '1.5rem',
          marginBottom: isMobile ? '1rem' : '1.5rem'
        }}>
          {/* Filters */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: isMobile ? '1.5rem' : '2rem'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              color: '#003366',
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: '600'
            }}>
              Filter
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Lotterie
                </label>
                <select
                  value={selectedLottery}
                  onChange={(e) => setSelectedLottery(e.target.value ? Number(e.target.value) : '')}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.95rem'
                  }}
                >
                  <option value="">Alle Lotterien</option>
                  {lotteries.map(lottery => (
                    <option key={lottery.id} value={lottery.id}>
                      {lottery.name_kurz} - {lottery.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Kampagne
                </label>
                <Autocomplete
                  options={filteredCampaigns}
                  getOptionLabel={(option) => {
                    const lottery = lotteries.find(l => l.id === option.lottery_id);
                    return selectedLottery
                      ? option.name
                      : `${option.name} (${lottery?.name_kurz || ''})`;
                  }}
                  filterOptions={(options, { inputValue }) => {
                    // Wenn Lotterie ausgewählt ist, zeige alle Kampagnen dieser Lotterie (oder filtere nach Eingabe)
                    if (selectedLottery) {
                      if (!inputValue || inputValue.trim().length === 0) return options;
                      const searchTerm = inputValue.toLowerCase().trim();
                      return options.filter(option =>
                        option.name.toLowerCase().startsWith(searchTerm)
                      );
                    }
                    // Ohne Lotterie: alle anzeigen (max 50), bei Eingabe filtern
                    if (!inputValue || inputValue.trim().length === 0) return options.slice(0, 50);
                    const searchTerm = inputValue.toLowerCase().trim();
                    return options.filter(option =>
                      option.name.toLowerCase().includes(searchTerm)
                    );
                  }}
                  value={allCampaigns.find(c => c.id === selectedCampaign) || null}
                  onChange={(_, newValue) => setSelectedCampaign(newValue ? newValue.id : '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedLottery ? "Kampagne wählen..." : "Kampagne suchen..."}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        }
                      }}
                    />
                  )}
                  noOptionsText={selectedLottery ? "Keine Kampagnen gefunden" : "Suchbegriff eingeben..."}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Werbeweg
                </label>
                <Autocomplete
                  options={filteredWerbewege}
                  getOptionLabel={(option) => option.name}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue || inputValue.trim().length === 0) return options.slice(0, 50);
                    const searchTerm = inputValue.toLowerCase().trim();
                    return options.filter(option =>
                      option.name.toLowerCase().includes(searchTerm)
                    );
                  }}
                  value={allWerbewege.find(w => w.id === selectedWerbeweg) || null}
                  onChange={(_, newValue) => setSelectedWerbeweg(newValue ? newValue.id : '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Werbeweg suchen..."
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        }
                      }}
                    />
                  )}
                  noOptionsText="Suchbegriff eingeben..."
                />
              </div>
            </div>
          </div>

          {/* Upload */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: isMobile ? '1.5rem' : '2rem'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              color: '#003366',
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: '600'
            }}>
              Dokument hochladen
            </h3>

            <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Datei *
                </label>
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Dokumentname
                </label>
                <input
                  type="text"
                  value={uploadData.document_name}
                  onChange={(e) => setUploadData({ ...uploadData, document_name: e.target.value })}
                  placeholder="Optional - Standard ist Dateiname"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Erstellungsdatum
                </label>
                <input
                  type="date"
                  value={uploadData.creation_date}
                  onChange={(e) => setUploadData({ ...uploadData, creation_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Autor
                </label>
                <input
                  type="text"
                  value={uploadData.author}
                  onChange={(e) => setUploadData({ ...uploadData, author: e.target.value })}
                  placeholder="Optional"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {uploadError && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}>
                  {uploadError}
                </div>
              )}

              <button
                type="submit"
                disabled={uploading || !selectedLottery}
                style={{
                  padding: '0.75rem',
                  backgroundColor: uploading || !selectedLottery ? '#9ca3af' : '#003366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploading || !selectedLottery ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
              </button>
            </form>
          </div>
        </div>

        {/* Documents List */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          padding: isMobile ? '1rem' : '1.5rem'
        }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            color: '#003366',
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: '600'
          }}>
            Dokumente ({documents.length})
          </h3>

          {/* Search and Sort Controls */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {/* Search */}
            <div style={{
              flex: isMobile ? '1 1 100%' : '0 0 250px',
              maxWidth: isMobile ? '100%' : '250px'
            }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#374151',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Suche
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, Autor..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Sort By */}
            <div style={{
              flex: isMobile ? '1 1 100%' : '0 0 180px',
              maxWidth: isMobile ? '100%' : '180px'
            }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#374151',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Sortieren nach
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'size' | 'author')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              >
                <option value="date">Datum</option>
                <option value="name">Name</option>
                <option value="size">Größe</option>
                <option value="author">Autor</option>
              </select>
            </div>

            {/* Sort Order */}
            <div style={{
              flex: isMobile ? '1 1 100%' : '0 0 160px',
              maxWidth: isMobile ? '100%' : '160px'
            }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#374151',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Reihenfolge
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              >
                <option value="asc">Aufsteigend</option>
                <option value="desc">Absteigend</option>
              </select>
            </div>
          </div>

          {/* Results Info */}
          {searchTerm && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              color: '#374151',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span>{filteredDocuments.length} von {documents.length} Dokumenten gefunden</span>
              {filteredDocuments.length < documents.length && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#003366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Filter zurücksetzen
                </button>
              )}
              {selectedDocuments.length > 0 && (
                <button
                  onClick={handleBulkCopyOpen}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    marginLeft: 'auto'
                  }}
                >
                  {selectedDocuments.length} Dokument(e) kopieren
                </button>
              )}
            </div>
          )}

          {/* Bulk copy button - shown when documents are selected */}
          {selectedDocuments.length > 0 && !searchTerm && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#dbeafe',
              borderRadius: '4px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: '0.9rem', color: '#1e40af' }}>
                {selectedDocuments.length} Dokument(e) ausgewählt
              </span>
              <button
                onClick={handleBulkCopyOpen}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#0066cc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                In andere Lotterie kopieren
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              Lädt...
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              {documents.length === 0 ? 'Keine Dokumente vorhanden' : 'Keine Dokumente gefunden'}
            </div>
          ) : isMobile ? (
            /* Mobile Card View */
            <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {paginatedDocuments.map((doc) => (
                <div
                  key={doc.id}
                  style={{
                    backgroundColor: selectedDocuments.includes(doc.id) ? '#f0f9ff' : 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedDocuments.includes(doc.id)}
                      onChange={() => handleSelectDocument(doc.id)}
                      style={{ cursor: 'pointer', marginTop: '4px' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', color: '#1f2937', wordBreak: 'break-word', marginBottom: '0.25rem' }}>
                        {doc.document_name}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {formatFileSize(doc.file_size)} • {formatDate(doc.creation_date || '')}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem',
                    fontSize: '0.8rem',
                    marginBottom: '0.75rem',
                    padding: '0.5rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '4px'
                  }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Kampagne: </span>
                      <span style={{ color: '#1f2937' }}>{stripLotterySuffix(doc.campaign_name || '') || '-'}</span>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Werbeweg: </span>
                      <span style={{ color: '#1f2937' }}>{doc.werbeweg_name || '-'}</span>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Autor: </span>
                      <span style={{ color: '#1f2937' }}>{doc.author || '-'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleDownload(doc)}
                      style={{
                        flex: '1 1 auto',
                        minWidth: '70px',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#003366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      ⬇
                    </button>
                    <button
                      onClick={() => handleEdit(doc)}
                      style={{
                        flex: '1 1 auto',
                        minWidth: '70px',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      ✏
                    </button>
                    <button
                      onClick={() => handleCopy(doc)}
                      style={{
                        flex: '1 1 auto',
                        minWidth: '70px',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📋
                    </button>
                    <button
                      onClick={() => handleDelete(doc)}
                      style={{
                        flex: '1 1 auto',
                        minWidth: '70px',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <TablePagination
              component="div"
              count={filteredDocuments.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 250]}
              labelRowsPerPage="Pro Seite:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
            />
            </>
          ) : (
            /* Desktop Table View */
            <div style={{
              overflowX: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
                tableLayout: 'fixed'
              }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'center', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={filteredDocuments.length > 0 && selectedDocuments.length === filteredDocuments.length}
                        onChange={handleSelectAll}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: '600', width: '180px', maxWidth: '180px' }}>Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: '600', width: '100px' }}>Kampagne</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: '600', width: '80px' }}>Werbeweg</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: '600', width: '60px' }}>Größe</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: '600', width: '100px' }}>Autor</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: '600', width: '90px' }}>Datum</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', color: '#374151', fontWeight: '600', width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: selectedDocuments.includes(doc.id) ? '#f0f9ff' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={() => handleSelectDocument(doc.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem', maxWidth: '180px' }}>
                        {editingDocument?.id === doc.id ? (
                          <input
                            type="text"
                            value={editData.document_name}
                            onChange={(e) => setEditData({ ...editData, document_name: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '0.85rem'
                            }}
                          />
                        ) : (
                          <TruncatedText text={doc.document_name} maxWidth="170px" />
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', color: '#6b7280', maxWidth: '100px' }}>
                        <TruncatedText text={stripLotterySuffix(doc.campaign_name || '') || '-'} maxWidth="90px" />
                      </td>
                      <td style={{ padding: '0.75rem', color: '#6b7280', maxWidth: '80px' }}>
                        <TruncatedText text={doc.werbeweg_name || '-'} maxWidth="70px" />
                      </td>
                      <td style={{ padding: '0.75rem', color: '#6b7280', maxWidth: '60px' }}>{formatFileSize(doc.file_size)}</td>
                      <td style={{ padding: '0.75rem', color: '#6b7280' }}>
                        {editingDocument?.id === doc.id ? (
                          <input
                            type="text"
                            value={editData.author}
                            onChange={(e) => setEditData({ ...editData, author: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '0.85rem'
                            }}
                          />
                        ) : (
                          doc.author || '-'
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', color: '#6b7280' }}>
                        {editingDocument?.id === doc.id ? (
                          <input
                            type="date"
                            value={editData.creation_date}
                            onChange={(e) => setEditData({ ...editData, creation_date: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '0.85rem'
                            }}
                          />
                        ) : (
                          formatDate(doc.creation_date || '')
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {editingDocument?.id === doc.id ? (
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={handleSaveEdit}
                              style={{
                                padding: '0.4rem 0.75rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              ✓ Speichern
                            </button>
                            <button
                              onClick={() => setEditingDocument(null)}
                              style={{
                                padding: '0.4rem 0.75rem',
                                backgroundColor: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setActionMenuAnchor(e.currentTarget);
                              setActionMenuDoc(doc);
                            }}
                          >
                            <DotsThreeVertical size={20} weight="bold" />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                component="div"
                count={filteredDocuments.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 250]}
                labelRowsPerPage="Pro Seite:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            </div>
          )}
        </div>

        {/* Action Menu */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={() => {
            setActionMenuAnchor(null);
            setActionMenuDoc(null);
          }}
        >
          <MenuItem onClick={() => {
            if (actionMenuDoc) handleDownload(actionMenuDoc);
            setActionMenuAnchor(null);
            setActionMenuDoc(null);
          }}>
            ⬇️ Download
          </MenuItem>
          <MenuItem onClick={() => {
            if (actionMenuDoc) handleEdit(actionMenuDoc);
            setActionMenuAnchor(null);
            setActionMenuDoc(null);
          }}>
            ✏️ Bearbeiten
          </MenuItem>
          <MenuItem onClick={() => {
            if (actionMenuDoc) handleCopy(actionMenuDoc);
            setActionMenuAnchor(null);
            setActionMenuDoc(null);
          }}>
            📋 Kopieren
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (actionMenuDoc) handleDelete(actionMenuDoc);
              setActionMenuAnchor(null);
              setActionMenuDoc(null);
            }}
            sx={{ color: '#dc2626' }}
          >
            🗑️ Löschen
          </MenuItem>
        </Menu>

        {/* Copy Dialog */}
        {copyingDocument && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? '1rem' : '2rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: isMobile ? '1.5rem' : '2rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{
                margin: '0 0 1rem 0',
                color: '#003366',
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                fontWeight: '600'
              }}>
                Dokument kopieren
              </h3>

              <div style={{
                padding: '1rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#374151' }}>
                  <strong>Dokument:</strong> {copyingDocument.document_name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Typ: {copyingDocument.document_type === 'werbemittel' ? 'Werbemittel' : 'Gesprächsleitfaden'}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Lotterie *
                  </label>
                  <select
                    value={copyData.lottery_id}
                    onChange={(e) => handleCopyLotteryChange(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Lotterie auswählen</option>
                    {copyLotteries.map(lottery => (
                      <option key={lottery.id} value={lottery.id}>
                        {lottery.name_kurz} - {lottery.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Kampagne
                  </label>
                  <select
                    value={copyData.campaign_id}
                    onChange={(e) => handleCopyCampaignChange(e.target.value)}
                    disabled={!copyData.lottery_id}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      opacity: copyData.lottery_id ? 1 : 0.5,
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Keine Kampagne</option>
                    {copyCampaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Werbeweg
                  </label>
                  <select
                    value={copyData.werbeweg_id}
                    onChange={(e) => setCopyData({ ...copyData, werbeweg_id: e.target.value })}
                    disabled={!copyData.campaign_id}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      opacity: copyData.campaign_id ? 1 : 0.5,
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Kein Werbeweg</option>
                    {copyWerbewege.map(werbeweg => (
                      <option key={werbeweg.id} value={werbeweg.id}>
                        {werbeweg.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  marginTop: '1rem',
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <button
                    onClick={handleSaveCopy}
                    disabled={copying || !copyData.lottery_id}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: copying || !copyData.lottery_id ? '#9ca3af' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: copying || !copyData.lottery_id ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500'
                    }}
                  >
                    {copying ? 'Wird kopiert...' : '✓ Kopieren'}
                  </button>
                  <button
                    onClick={() => {
                      setCopyingDocument(null);
                      setCopyData({ lottery_id: '', campaign_id: '', werbeweg_id: '' });
                      setCopyLotteries([]);
                      setCopyCampaigns([]);
                      setCopyWerbewege([]);
                    }}
                    disabled={copying}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: copying ? '#9ca3af' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: copying ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500'
                    }}
                  >
                    ✕ Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Copy Dialog */}
        {bulkCopyDialog && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? '1rem' : '2rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: isMobile ? '1.5rem' : '2rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{
                margin: '0 0 1rem 0',
                color: '#003366',
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                fontWeight: '600'
              }}>
                Dokumente kopieren
              </h3>

              <div style={{
                padding: '1rem',
                backgroundColor: '#dbeafe',
                borderRadius: '4px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#1e40af' }}>
                  <strong>{selectedDocuments.length} Dokument(e)</strong> werden kopiert
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Ziel-Lotterie *
                  </label>
                  <select
                    value={copyData.lottery_id}
                    onChange={(e) => handleCopyLotteryChange(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Lotterie auswählen</option>
                    {copyLotteries.map(lottery => (
                      <option key={lottery.id} value={lottery.id}>
                        {lottery.name_kurz} - {lottery.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Kampagne
                  </label>
                  <select
                    value={copyData.campaign_id}
                    onChange={(e) => handleCopyCampaignChange(e.target.value)}
                    disabled={!copyData.lottery_id}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      opacity: copyData.lottery_id ? 1 : 0.5,
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Keine Kampagne</option>
                    {copyCampaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Werbeweg
                  </label>
                  <select
                    value={copyData.werbeweg_id}
                    onChange={(e) => setCopyData({ ...copyData, werbeweg_id: e.target.value })}
                    disabled={!copyData.campaign_id}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      opacity: copyData.campaign_id ? 1 : 0.5,
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Kein Werbeweg</option>
                    {copyWerbewege.map(werbeweg => (
                      <option key={werbeweg.id} value={werbeweg.id}>
                        {werbeweg.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  marginTop: '1rem'
                }}>
                  <button
                    onClick={handleBulkCopy}
                    disabled={copying || !copyData.lottery_id}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: copying || !copyData.lottery_id ? '#9ca3af' : '#0066cc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: copying || !copyData.lottery_id ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500'
                    }}
                  >
                    {copying ? 'Wird kopiert...' : `${selectedDocuments.length} kopieren`}
                  </button>
                  <button
                    onClick={() => {
                      setBulkCopyDialog(false);
                      setCopyData({ lottery_id: '', campaign_id: '', werbeweg_id: '' });
                      setCopyLotteries([]);
                      setCopyCampaigns([]);
                      setCopyWerbewege([]);
                    }}
                    disabled={copying}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: copying ? '#9ca3af' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: copying ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500'
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
